import { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import moment from 'moment'
import { Chip, Typography, Tooltip, IconButton, Stack } from '@mui/material'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import { EditUploadLineSpendBasedDialog } from '../../../dialogs/settings/data-import/edit-upload-line-spend-based-dialog'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import { NumericFormat } from 'react-number-format'
import { IEditFileUploadLineSpendBased, IFileUploadLineSpendBased } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'
import { useNavigate } from 'react-router-dom'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { EditOutlined } from '@mui/icons-material'

export const CsvUploadSpendBased: FunctionComponent<{
  uploadId?: string;
  fileUploadLineSpendBaseds: IFileUploadLineSpendBased[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uploadLine: IFileUploadLineSpendBased) => void,
  deleteHandler: (id: string, status?: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineSpendBased | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineSpendBased) => void,
  status: string,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  errorCode?: number,
  errorMessage?: string,
  selectAllFiltered?: boolean,
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void,
}> = ({
  uploadId,
  fileUploadLineSpendBaseds,
  editClickHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setOrder,
  setOrderBy,
  setPerPage,
  setSelected,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  status,
  deleteHandler,
  isSubmitting,
  errorCode,
  errorMessage,
  selectAllFiltered,
  onExitSelectAllFiltered,
}) => {
    const navigate = useNavigate()
    //Service
    const contactSupplierService = ContactSupplierService.getInstance();
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [allContactSuppliers, setAllContactSuppliers] = useState<IContactSupplierDetails[]>()

    useEffect(() => {
      contactSupplierService.getActiveSuppliersLite(false)
        .then(response => {
          setAllContactSuppliers(response)
        })
        .catch(err => console.log(err))
    }, [])

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []
        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''
        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''
        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : status === 'pending' ? (
          <Chip
            color={uploadLine.commitStatus === 'FAILED' ? 'error' : 'success'}
            label={uploadLine.commitStatus === 'FAILED' ? 'Failed' : 'Ready'}
            size="small"
            variant="outlined"
          />
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          supplier: uploadLine.contactName || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'supplier', numeric: false, label: 'Supplier', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...((status !== 'discarded' && ((status === 'completed') || ((status === 'pending' || status === 'validated') && !isReportPeriodLocked))) ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'contactName', numeric: false, label: screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }), sortable: true },
        { id: 'groupName', numeric: false, label: 'Group', sortable: true },
        { id: 'spendBasedConversionName', numeric: false, label: 'Activity', sortable: true },
        // { id: 'confidence', numeric: false, label: 'Activity Confidence', sortable: false },
        { id: 'total', numeric: true, label: 'Total (£)', sortable: true },
        ...(status === 'completed' ? [{ id: 'emissionAmount', numeric: false, label: 'Emission Amount (kgCO2e)', sortable: false }] : []),
        { id: 'invoiceDate', numeric: false, label: 'Invoice Date', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineSpendBaseds || [])
      : fileUploadLineSpendBaseds?.map((uploadLine: IFileUploadLineSpendBased) => {
        return {
          id: uploadLine.id,
          groupName: uploadLine.groupName,
          contactName: uploadLine.contactName,
          confidence:
            uploadLine.selectedByUser ? <Chip variant='outlined' color='success' label="User Verified" sx={{ width: '100px' }} id={'1'} /> :
              allContactSuppliers?.find(supplier => supplier.name === uploadLine.contactName)?.spendBasedConversionId && uploadLine.spendBasedConversionName ? <Chip variant='outlined' color='success' label="User Verified" sx={{ width: '100px' }} id={'1'} /> :
                uploadLine.spendBasedConversionName && uploadLine.numberOfCompanyHouseReturns === 1 ? <Chip variant='outlined' color='success' label="High" sx={{ width: '100px' }} id={'2'} /> :
                  uploadLine.spendBasedConversionName && uploadLine.numberOfCompanyHouseReturns > 1 && uploadLine.numberOfCompanyHouseReturns < 6 ? <Chip variant='outlined' color='warning' label="Medium" sx={{ width: '100px' }} id={'3'} /> :
                    uploadLine.spendBasedConversionName && uploadLine.numberOfCompanyHouseReturns > 5 && uploadLine.numberOfCompanyHouseReturns < 11 ? <Chip variant='outlined' color='warning' label="Low" sx={{ width: '100px' }} id={'4'} /> :
                      uploadLine.spendBasedConversionName && uploadLine.numberOfCompanyHouseReturns > 10 ? <Chip variant='outlined' color='error' label="Very Low" sx={{ width: '100px' }} id={'5'} /> :
                        <Typography id={'6'}>{null}</Typography>,
          spendBasedConversionName: `${uploadLine.spendBasedConversionName}`,
          total: (<NumericFormat value={uploadLine.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale />),
          emissionAmount: (< NumericFormat value={uploadLine.emissionAmount} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale />),
          invoiceDate: moment(uploadLine.invoiceDate).format('DD/MM/YYYY'),
          action: status === 'validated' && !isReportPeriodLocked ? (
            <TableActionButtons
              id={uploadLine.id}
              includeEdit={true}
              includeDelete={true}
              onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
              onDelete={(id: string) => deleteHandler(id, status)}
            />
          ) : status === 'completed' ? (
            <TableActionButtons
              id={uploadLine.id}
              includeView={true}
              onView={() => uploadLine.emissionId ? viewTransactionClickHandler(uploadLine.emissionId) : undefined}
              disableButton={!uploadLine.emissionId}
            />
          ) : null
        }
      })

    const viewTransactionClickHandler = (transactionId: string) => {
      navigate(`/emissions/overview/${transactionId}?backLink=${encodeURIComponent(`/settings/data-upload/import-history/spend-based/${uploadId}?tabId=1`)}&backText=${encodeURIComponent('Back to import')}`)
    }

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => {
        return {
          id: row
        }
      })
      setValidatedUploadLines(formatted)
    }


    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selectByIcon={(status === 'validated' || status === 'pending') && !isReportPeriodLocked ? false : true}
          selectByCheckboxOnly={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          onOrderChange={handleOrderChange}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          selectAllFiltered={selectAllFiltered}
          onExitSelectAllFiltered={onExitSelectAllFiltered}
          inputPage={inputPage}
          haveCheckboxSelect={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        {editUploadLine && openDialog && !isReportPeriodLocked &&
          <EditUploadLineSpendBasedDialog
            open={openDialog}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine}
            errorCode={errorCode || 0}
            errorMessage={errorMessage || ''} />}
      </>
    )
  }